import CustomTooltip from "@Components/common/charts/custom-tooltip";
import LineAxisTick from "@Components/common/charts/line-axis-tick";
import { blendWithWhite } from "@Helpers/color-utils";
import { Skeleton, Stack, useTheme } from "@mui/material";
import React, { FC, useCallback, useRef } from "react";
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const MIN_HEIGHT_FOR_TOOLTIP = 150;

export interface IProgressChartDataPoint {
  name: string;
  score: number | null;
  placeholderScore?: number | null;
  tooltipScore?: number | null;
  activeDotIndex?: number | null;
  [key: string]: any;
}

interface IProgressChart {
  data: IProgressChartDataPoint[];
  height?: number;
  actionButton?: React.ReactNode;
  loading?: boolean;
  placeholder?: React.ReactNode;
  color?: string;
  tooltipDecimals?: number;
  hideXAxis?: boolean;
  chartId?: string;
  showAverageScore?: boolean;
  showUnit?: boolean;
  showTooltipCursor?: boolean;
}

const ProgressChart: FC<IProgressChart> = ({
  data,
  height = 100,
  actionButton,
  loading = false,
  placeholder,
  color,
  tooltipDecimals = 2,
  hideXAxis = false,
  chartId = "default",
  showAverageScore = false,
  showUnit = false,
  showTooltipCursor = false,
}) => {
  const theme = useTheme();
  const color_ = color || theme.palette.dsPrimary.v300;
  const dotCoordinatesRef = useRef<Map<number, { cx: number; cy: number }>>(new Map());

  const scores = data.map((d: any) => d.score).filter((s: number | null) => s !== null);
  const hasValidScores = scores.length > 0;

  const minScore = hasValidScores ? Math.min(...scores) : 0;
  const maxScore = hasValidScores ? Math.max(...scores) : 100;
  const averageScore = hasValidScores ? scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length : 0;
  const range = maxScore - minScore;
  const padding = range * 0.25;

  const areScoresConstant = hasValidScores && scores.every((score: number) => score === scores[0]);

  const yAxisDomain: [number, number] = [Math.max(0, minScore - padding), maxScore + padding];

  const gradientIds = {
    fill: `metric-fill-${chartId}`,
    placeholderFill: `placeholder-fill-${chartId}`,
    stroke: `stroke-gradient-${chartId}`,
    refLine: `ref-line-gradient-${chartId}`,
    boundary: `boundary-line-gradient-${chartId}`,
  };

  const isDataFullyLabelled = data.every(entry => entry.name && entry.name.trim().length > 0);

  const processedData: IProgressChartDataPoint[] = (() => {
    if (data.length === 0) return [];

    const firstNonNullIndex = data.findIndex(d => d.score !== null && d.score !== undefined);
    const lastNonNullIndex = [...data].reverse().findIndex(d => d.score !== null && d.score !== undefined);
    const lastNonNullIndexFromStart = lastNonNullIndex !== -1 ? data.length - 1 - lastNonNullIndex : -1;

    const firstNonNullValue = firstNonNullIndex !== -1 ? data[firstNonNullIndex].score : null;
    const lastNonNullValue = lastNonNullIndexFromStart !== -1 ? data[lastNonNullIndexFromStart].score : null;

    let lastKnownScore: number | null = null;
    let lastKnownScoreIndex: number | null = null;

    return data.map((item, index) => {
      let placeholderScore = null;
      let tooltipScore: number | null = null;
      let activeDotIndex: number | null = null;

      if (firstNonNullIndex !== -1 && index <= firstNonNullIndex && firstNonNullValue !== null) {
        placeholderScore = firstNonNullValue;
      } else if (lastNonNullIndexFromStart !== -1 && index >= lastNonNullIndexFromStart && lastNonNullValue !== null) {
        placeholderScore = lastNonNullValue;
      }

      if (item.score !== null && item.score !== undefined) {
        lastKnownScore = item.score;
        lastKnownScoreIndex = index;
        tooltipScore = item.score;
        activeDotIndex = index;
      } else if (lastKnownScore !== null && lastKnownScoreIndex !== null) {
        tooltipScore = lastKnownScore;
        activeDotIndex = lastKnownScoreIndex;
      } else if (firstNonNullValue !== null && firstNonNullIndex !== -1) {
        tooltipScore = firstNonNullValue;
        activeDotIndex = firstNonNullIndex;
      }

      return {
        ...item,
        placeholderScore,
        tooltipScore,
        activeDotIndex,
      };
    });
  })();

  const CustomDot = useCallback(
    (props: any) => {
      const { cx, cy, index, payload } = props;

      if (cx !== undefined && cy !== undefined && index !== undefined) {
        dotCoordinatesRef.current.set(index, { cx, cy });
      }

      if (!payload || payload.score === null || payload.score === undefined) {
        return <circle r={0} fill="transparent" />;
      }

      return <circle cx={cx} cy={cy} r={0} fill={color_} />;
    },
    [color_]
  );

  const CustomActiveDot = useCallback(
    (props: any) => {
      const { payload, index, cx, cy } = props;

      if (!payload || hideXAxis || index === undefined || cx === undefined || cy === undefined) {
        return <circle r={0} fill="transparent" />;
      }

      const targetIndex = payload.activeDotIndex;
      if (targetIndex === undefined || targetIndex === null) {
        return <circle r={0} fill="transparent" />;
      }

      const targetDataPoint = processedData[targetIndex];
      if (!targetDataPoint || targetDataPoint.score === null || targetDataPoint.score === undefined) {
        return <circle r={0} fill="transparent" />;
      }

      const storedCoords = dotCoordinatesRef.current.get(targetIndex);
      if (!storedCoords) {
        return <circle r={0} fill="transparent" />;
      }

      return <circle cx={storedCoords.cx} cy={storedCoords.cy} r={6} fill={color_} stroke="white" strokeWidth={2} />;
    },
    [hideXAxis, processedData, color_]
  );

  return (
    <Stack width="100%" height={height}>
      {actionButton && (
        <Stack alignItems="flex-end" mb={1}>
          {actionButton}
        </Stack>
      )}
      {loading ? (
        <Stack alignItems="center" justifyContent="center" width="100%" flex={1} paddingBottom={theme.spacing(1.5)}>
          <Skeleton variant="rounded" width="100%" height="100%" animation="wave" />
        </Stack>
      ) : (
        <>
          {data.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" className="swiper-no-swiping">
              <AreaChart data={processedData}>
                <XAxis
                  dataKey="name"
                  fontSize={theme.spacing(1.5)}
                  interval={0}
                  fontWeight={500}
                  stroke={theme.palette.dsNeutral.v300}
                  tickLine={false}
                  axisLine={false}
                  tick={hideXAxis ? false : LineAxisTick}
                  hide={hideXAxis}
                />
                <YAxis domain={yAxisDomain} padding={{ bottom: Math.max(range / 10, height) * 0.05 }} hide />
                <defs>
                  <linearGradient id={gradientIds.fill} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color_} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={color_} stopOpacity={0.025} />
                  </linearGradient>
                  <linearGradient id={gradientIds.placeholderFill} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.dsNeutral.v300} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={theme.palette.dsNeutral.v300} stopOpacity={0.025} />
                  </linearGradient>
                  <linearGradient id={gradientIds.stroke} gradientUnits="userSpaceOnUse" x1="0" y1={height} x2="0" y2="0">
                    <stop offset="0%" stopColor={blendWithWhite(color_, 0.1)} />
                    <stop offset="100%" stopColor={color_} />
                  </linearGradient>
                  <linearGradient id={gradientIds.refLine} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={height}>
                    <stop offset="5%" stopColor={color_} stopOpacity={0.1} />
                    <stop offset="95%" stopColor={color_} stopOpacity={0.2} />
                  </linearGradient>
                  <linearGradient id={gradientIds.boundary} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={height}>
                    <stop offset="5%" stopColor={color_} stopOpacity={0.05} />
                    <stop offset="95%" stopColor={color_} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                {processedData.map((entry, index) =>
                  entry.name || index === 0 || index === processedData.length - 1 ? (
                    <ReferenceLine
                      key={`ref-line-${index}`}
                      x={isDataFullyLabelled ? entry.name : index}
                      stroke={index === 0 || index === processedData.length - 1 ? `url(#${gradientIds.boundary})` : `url(#${gradientIds.refLine})`}
                      strokeWidth={1}
                      strokeDasharray="0"
                      isFront={false}
                    />
                  ) : null
                )}
                {showAverageScore && hasValidScores && (
                  <ReferenceLine key="average-score" y={averageScore} stroke={`${color_}85`} strokeWidth={1} strokeDasharray="4 4" isFront={false} />
                )}
                <Area
                  type="monotone"
                  dataKey="placeholderScore"
                  stroke={theme.palette.dsNeutral.v400}
                  strokeWidth={hideXAxis ? 0.5 : 1}
                  fill={`url(#${gradientIds.placeholderFill})`}
                  dot={false}
                  activeDot={false}
                  connectNulls={false}
                  strokeDasharray="4 4"
                />
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke={areScoresConstant ? color_ : `url(#${gradientIds.stroke})`}
                  strokeWidth={hideXAxis ? 1.5 : 2.5}
                  fill={`url(#${gradientIds.fill})`}
                  dot={CustomDot}
                  connectNulls={true}
                />
                {!hideXAxis && (
                  <Area
                    type="monotone"
                    dataKey="tooltipScore"
                    stroke="transparent"
                    strokeWidth={0}
                    fill="transparent"
                    dot={false}
                    activeDot={CustomActiveDot}
                    connectNulls={false}
                  />
                )}
                {height > MIN_HEIGHT_FOR_TOOLTIP && (
                  <Tooltip
                    content={<CustomTooltip color={color_} decimals={tooltipDecimals} metricType={chartId} showUnit={showUnit} />}
                    wrapperStyle={{
                      outline: "none",
                    }}
                    cursor={showTooltipCursor}
                    isAnimationActive
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            placeholder
          )}
        </>
      )}
    </Stack>
  );
};

export default ProgressChart;
